<template>
  <div class="winners-winners-section__categories">
    <div
      v-for="(mainCategory, mainCategoryName) in winners"
      :key="mainCategoryName"
      class="winners-winners-section__categories-main-category-wrapper"
    >
      <h3 class="winners-winners-section__categories-main-category-title">
        {{ getCategoryTitle(mainCategoryName) }}
      </h3>

      <div
        v-for="(subCategory, subCategoryName) in mainCategory"
        :key="subCategoryName"
        class="winners-winners-section__categories-category-wrapper"
      >
        <div class="winners-winners-section__categories-header">
          <h2 class="title-new uppercase">
            {{ formatSubCategoryName(subCategoryName) }}
          </h2>
          <p class="winners-winners-section__categories-subtitle">
            {{ getCategorySubTitle(mainCategoryName) }}
          </p>
        </div>
        <div class="winners-winners-section__categories-brands">
          <div
            class="winners-winners-section__categories-brands-item"
            v-for="brand in subCategory.Brands"
            :key="brand.Id"
          >
            <div class="winners-winners-section__categories-brands-header">
              <p class="winners-winners-section__categories-brands-name">
                {{ brand.Name }}
              </p>
              <a-image
                :src="$helper.getLinkToFmBucketFile(brand.LogoUrl)"
                :alt="brand.Name"
                class="winners-winners-section__categories-brands-img"
              />
            </div>
            <div class="winners-winners-section__categories-label">
              {{ brand.WinningCategory }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PROP_TYPES, propValidator } from '@fmpedia/validators'
import { VOTING_CATEGORY_TITLE_DATA_BY_CATEGORY_TYPE } from 'sections/_voting/VotingVoteSection/VotingVoteForm/enums'

export default {
  name: 'WinnersWinnersSectionCategories',
  props: {
    winners: propValidator([PROP_TYPES.OBJECT])
  },
  methods: {
    getCategoryTitle(categoryName) {
      return VOTING_CATEGORY_TITLE_DATA_BY_CATEGORY_TYPE[categoryName].title
    },
    getCategorySubTitle(categoryName) {
      return VOTING_CATEGORY_TITLE_DATA_BY_CATEGORY_TYPE[categoryName].subtitle
    },
    // We need this function to format the sub category name - add spaces between words
    formatSubCategoryName(name) {
      return name.replace(/([A-Z])/g, ' $1').trim()
    }
  }
}
</script>

<style lang="scss" scoped>
.winners-winners-section__categories {
  .winners-winners-section__categories-main-category-title {
    color: $c--white;
    font-style: normal;
    font-weight: 400;
    text-transform: uppercase;

    $margin-bottom: (
      $above-desktop-xl: 80px,
      $mobile: 48px
    );

    $font-size: (
      $above-desktop-xl: 48px,
      $tablet: 32px,
      $mobile: 24px
    );

    $line-height: (
      $above-desktop-xl: 60px,
      $tablet: 32px
    );

    @include setStyleByBreakpoint('margin-bottom', $margin-bottom);
    @include setStyleByBreakpoint('font-size', $font-size);
    @include setStyleByBreakpoint('line-height', $line-height);

    &:before {
      margin-bottom: 33px;
    }

    &:after {
      margin-top: 33px;
    }

    &:before,
    &:after {
      content: '';
      background: linear-gradient(
        270deg,
        #1a15cb 0%,
        #8641e9 50%,
        #88e4fb 100%
      );
      height: 2px;
      width: 100%;
      display: block;
    }
  }

  .winners-winners-section__categories-category-wrapper {
    $margin-bottom: (
      $above-desktop-xl: 128px,
      $mobile: 64px
    );

    @include setStyleByBreakpoint('margin-bottom', $margin-bottom);
  }

  .winners-winners-section__categories-header {
    justify-content: space-between;

    border-bottom: 1px solid $c--gray;
    display: flex;

    $align-items: (
      $above-desktop-xl: center,
      $mobile: flex-start
    );

    $flex-direction: (
      $above-desktop-xl: row,
      $mobile: column
    );

    $padding-bottom: (
      $above-desktop-xl: 32px
    );

    @include setStyleByBreakpoint('align-items', $align-items);
    @include setStyleByBreakpoint('flex-direction', $flex-direction);
    @include setStyleByBreakpoint('padding-bottom', $padding-bottom);
  }

  .winners-winners-section__categories-subtitle {
    color: $c--white;
    font-size: 18px;
    font-weight: 400;
    line-height: 28px;

    @include mobile {
      margin-top: 10px;
    }
  }

  .winners-winners-section__categories-brands {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 32px;
    margin-top: 32px;

    @include tablet {
      grid-template-columns: repeat(2, 1fr);
    }

    @include mobile {
      grid-template-columns: 1fr;
    }
  }

  .winners-winners-section__categories-brands-item {
    background: $c--background-dark-7;
    border: 2px solid $c--background-dark-7;
    border-radius: 12px;
    padding: 24px;

    &:nth-child(5n + 1),
    &:nth-child(5n + 2),
    &:nth-child(5n + 3) {
      grid-column: span 2;
    }

    &:nth-child(5n + 4),
    &:nth-child(5n + 5) {
      grid-column: span 3;
    }

    @include tablet {
      padding: 24px;

      grid-column: span 1;

      &:nth-child(3n + 1),
      &:nth-child(3n + 2) {
        grid-column: span 1;
      }

      &:nth-child(3n + 3) {
        grid-column: span 2;
      }
    }

    @include mobile {
      grid-column: span 1;

      &:nth-child(3n + 1),
      &:nth-child(3n + 2),
      &:nth-child(3n + 3) {
        grid-column: span 1;
      }
    }

    &--error {
      border: 2px solid $c--error;
    }
  }

  .winners-winners-section__categories-brands-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    @include mobile {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .winners-winners-section__categories-brands-img {
    margin-left: 5px;
    border-radius: 4px;
    border: 1px solid rgba($c--white, 0.1);
    width: 128px;
  }

  .winners-winners-section__categories-brands-name {
    color: $c--white;
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;

    @include mobile {
      margin-bottom: 16px;
    }
  }

  .winners-winners-section__categories-label {
    color: $c--white;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 28px;
  }
}
</style>
